import { useMediaClient, useScopedMediaClientCache } from '@databases/media-client';
import { useDataUrl } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { DatabaseAttachment } from '@databases/types/database';
import { getMediaCollectionId } from '@databases/utils/media-client';
import { AsyncOperation, AsyncOperationStatus } from '@databases/utils/redux/redux';
import { isBlob, serializeError } from '@databases/utils/utils';
import { useCallback, useMemo } from 'react';
import { useAsync } from 'react-async';

/**
 * Returns an async operation object that, when succeeded, simply contains the given image URL.
 * If the given image URL starts with the current Confluence base URL, it is loaded into a
 * data URL using useDatabaseAttachmentDataUrl() instead.
 * If the given image is undefined, undefined is returned.
 */
function useExternalImageUrl(
  image: { src: string } | undefined
): AsyncOperation<string> | undefined {
  return useMemo((): AsyncOperation<string> | undefined => {
    if (image) {
      // Attachment src does not start with base URL (for example in case of an import)
      return { status: AsyncOperationStatus.SUCCEEDED, data: image.src };
    } else {
      return undefined;
    }
  }, [image]);
}

/**
 * Returns an async operation object that, when succeeded, contains a data URL represeting the
 * content of an image that is stored through the Media API.
 * If the given image is undefined, undefined is returned.
 */
export function useMediaApiUrl(
  file: { fileId: string } | undefined,
  contentId: string | undefined
): AsyncOperation<string> | undefined {
  const mediaClient = useMediaClient(contentId);
  const store = defaultStoreUtils.useStore();
  const urlAsync = useAsync({
    promiseFn: useCallback(async () => {
      if (mediaClient && file && contentId) {
        const mediaCollectionId = await getMediaCollectionId(store.getState(), contentId);
        if (mediaCollectionId) {
          return await mediaClient?.getImageUrl(file.fileId, {
            collection: mediaCollectionId,
          });
        }
      }
    }, [contentId, file, mediaClient, store]),
  });

  if (!file) {
    return undefined;
  } else if (urlAsync.isResolved) {
    if (urlAsync.data) {
      return { status: AsyncOperationStatus.SUCCEEDED, data: urlAsync.data };
    } else {
      return undefined;
    }
  } else if (urlAsync.isRejected) {
    return { status: AsyncOperationStatus.ERROR, error: serializeError(urlAsync.error) };
  } else if (urlAsync.isPending) {
    return { status: AsyncOperationStatus.PENDING };
  } else {
    return { status: AsyncOperationStatus.INITIAL };
  }
}

/**
 * Returns an async operation object that contains a data URL representing the content of the
 * given blob.
 * If the given blob is undefined, undefined is returned.
 */
function useBlobDataUrl(blob: Blob | undefined): AsyncOperation<string> | undefined {
  const dataUrl = useDataUrl(blob);

  return useMemo((): AsyncOperation<string> | undefined => {
    if (dataUrl) {
      return { status: AsyncOperationStatus.SUCCEEDED, data: dataUrl };
    } else {
      return undefined;
    }
  }, [dataUrl]);
}

/**
 * Returns an async operation object that, when succeeded, contains a URL representing the image content of
 * the given database attachment, blob, external image URL or Media API file ID. If possible, the image is
 * downloaded using the appropriate mechanism and converted into a data URL to make it possible to render
 * it as part of an <img> tag without relying on the availability of third-party cookies for authentication.
 */
export function useAttachmentData(
  attachment:
    | DatabaseAttachment
    | Blob
    | { src: string }
    | { fileId: string }
    | undefined,
  contentId: string | undefined
): AsyncOperation<string> {
  const externalUrlResult = useExternalImageUrl(
    typeof attachment === 'object' && 'src' in attachment ? attachment : undefined
  );

  const mediaApiResult = useMediaApiUrl(
    typeof attachment === 'object' && 'fileId' in attachment ? attachment : undefined,
    contentId
  );

  const blobResult = useBlobDataUrl(isBlob(attachment) ? attachment : undefined);

  return useMemo((): AsyncOperation<string> => {
    return (
      externalUrlResult ||
      mediaApiResult ||
      blobResult || { status: AsyncOperationStatus.INITIAL }
    );
  }, [blobResult, externalUrlResult, mediaApiResult]);
}

export function useMediaApiFileType(
  file: { fileId: string } | undefined,
  contentId: string
): AsyncOperation<string> | undefined {
  const mediaClient = useMediaClient(contentId);
  const store = defaultStoreUtils.useStore();
  const cache = useScopedMediaClientCache(contentId);

  const urlAsync = useAsync({
    promiseFn: useCallback(async () => {
      if (mediaClient && file) {
        const mediaCollectionId = await getMediaCollectionId(store.getState(), contentId);

        if (mediaCollectionId) {
          if (cache.has(file.fileId)) {
            return cache.get(file.fileId);
          }

          const fileData = await mediaClient.mediaStore.getFile(file.fileId, {
            collection: mediaCollectionId,
          });

          const mediaType = fileData.data.mediaType;

          cache.set(file.fileId, mediaType);

          return mediaType;
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentId, file, mediaClient, store]),
  });

  if (!file) {
    return undefined;
  } else if (urlAsync.isResolved) {
    if (urlAsync.data) {
      return { status: AsyncOperationStatus.SUCCEEDED, data: urlAsync.data };
    } else {
      return undefined;
    }
  } else if (urlAsync.isRejected) {
    return { status: AsyncOperationStatus.ERROR, error: serializeError(urlAsync.error) };
  } else if (urlAsync.isPending) {
    return { status: AsyncOperationStatus.PENDING };
  } else {
    return { status: AsyncOperationStatus.INITIAL };
  }
}
